import Api from '../Api'
import { INFTSalesListFilter } from '../../pages/NFTMarketPlace/models/INFTSalesList'
import { NFTSalesFilterHelper } from '../../pages/NFTMarketPlace/utils/NFTSalesFilterHelper'
import { NFT_ERC721_GRAPH_URL, NFT_SUB_GRAPH_URL } from '../../constants/service'
import { COLLECTION, MARKET_PLACE, USER_PROFILE } from '../../pages/NFTMarketPlace/data/NFTMarketPerPages'

async function BaseGraphql(queryString: string, url?: string) {
  return await Api.post(url ? url : NFT_SUB_GRAPH_URL, { query: `{${queryString}}` })
}

async function getBlackList() {
  return await fetch('./blackList_2.json', {
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json'
    }
  }).then(result => result.json())
}

function getSalesNFTListFilterQuery(
  tokenBlackList: number[],
  sellerBlackList: string[],
  creatorBlackList: string[],
  filter?: INFTSalesListFilter,
) {
  const creatorListJoin = creatorBlackList.join(',')
  let result = `creator_not_in: [${creatorListJoin}]`
  if (filter === undefined || (filter && filter.categoryId === null)) {
    result = `,
        category_not_in: [5],
        uniqueTokenId_not_in: [${tokenBlackList.join(',')}],
        seller_not_in: [${sellerBlackList.join(',')}],
        creator_not_in: [${creatorListJoin}]
        `
  } else if (filter && filter.categoryId === 5) {
    result = ''
  }

  if (filter && filter.currency !== 'ALL') {
    result = `saleToken: "${filter.currency}"`
  }

  return result
}

function getSalesNFTSortQuery(sortItems?: string) {
  let result = 'orderBy: insertDate, orderDirection: desc'

  if (sortItems) {
    if (sortItems === 'NewestItems') {
      result = 'orderBy: insertDate, orderDirection: desc'
    } else if (sortItems === 'OldestItems') {
      result = 'orderBy: insertDate, orderDirection: asc'
    } else if (sortItems === 'PriceLowToHigh') {
      result = 'orderBy: price, orderDirection: asc'
    } else if (sortItems === 'PriceHighToLow') {
      result = 'orderBy: price, orderDirection: desc'
    }
  }

  return result
}

function getSalesNotInNFTListFilterQuery(
  tokenBlackList: number[],
  sellerBlackList: string[],
  creatorBlackList: string[],
  filter?: INFTSalesListFilter
) {
  const creatorListJoin = creatorBlackList.join(',')
  let result = `creator_in: [${creatorListJoin}]`
  if (filter === undefined || (filter && filter.categoryId === null)) {
    result = `,
        creator_in: [${creatorListJoin}]
        `
  } else if (filter && filter.categoryId === 5) {
    result = ''
  }

  return result
}

export async function getSalesNFTList(filter: INFTSalesListFilter) {
  const getFilterQuery = NFTSalesFilterHelper(filter, filter.collection)
  const getBlackListData = await getBlackList()
  const tokenBlackList = getBlackListData.token.map(
    (item: number) => `"0x9c39fb419556a472a12ce5835c8b4c28a7868257:${item}"`
  )

  const filterControl = getSalesNFTListFilterQuery(
    tokenBlackList,
    getBlackListData.seller,
    getBlackListData.creator,
    filter
  )

  const nftListQuery = ` saleNFTs(
      where: {
        status: 1 ${getFilterQuery},
        ${filterControl}
      } 
      ${getSalesNFTSortQuery(filter.sortItems)}
      first: ${filter.perPage}, skip: ${filter.skipItems}
    ) {
    id
    tokenContract
    tokenId
    price
    category
    seller
    saleIndex
    marketContract
    saleToken
  }
  `

  const result = await BaseGraphql(nftListQuery)

  return result.data.saleNFTs
}

export async function getNotInSalesNFTList(filter?: INFTSalesListFilter) {
  const getFilterQuery = NFTSalesFilterHelper(filter)
  const getBlackListData = await getBlackList()
  const tokenBlackList = getBlackListData.token.map(
    (item: number) => `"0x9c39fb419556a472a12ce5835c8b4c28a7868257:${item}"`
  )
  const filterControl = getSalesNotInNFTListFilterQuery(
    tokenBlackList,
    getBlackListData.seller,
    getBlackListData.creator,
    filter
  )
  const nftListQuery = ` saleNFTs(
      where: {
        status: 1 ${getFilterQuery},
        ${filterControl}
      } 
      orderBy: insertDate, orderDirection: desc
      first: 1000
    ) {
    id
    tokenContract
    tokenId
    price
    category
    seller
    saleIndex
    marketContract
    saleToken
  }
  `

  const result = await BaseGraphql(nftListQuery)
  const { saleNFTs } = result.data
  return saleNFTs.length
}

export async function getNFTItem(tokenId: number, tokenContract: string) {
  const nftListQuery = ` saleNFTs(where: {tokenContract: "${tokenContract}" tokenId: ${tokenId}} orderBy: insertDate, orderDirection: desc) {
    id
    tokenContract
    tokenId
    price
    seller
    category
    status
    buyer
    saleToken
    saleIndex
    marketContract
    insertDate
  }`

  const result = await BaseGraphql(nftListQuery)
  return result.data.saleNFTs
}

export async function getNFTItemFromERC721(tokenId: number, tokenContract: string) {
  const nftListQuery = `  tokens(where: {contract: "${tokenContract.toLowerCase()}" tokenID:"${tokenId}"}) {
    id
    contract {
      id
    }
    tokenID
    tokenURI
    owner {
      id
    }
  }`

  const result = await BaseGraphql(nftListQuery, NFT_ERC721_GRAPH_URL)
  let tokenItem = undefined

  if (result && result.data && result.data.tokens && result.data.tokens.length > 0) {
    tokenItem = result.data.tokens[0]
  }

  return tokenItem
}

export async function getAccountOnSaleNFTItems(account: string) {
  const nftListQuery = ` saleNFTs(where: {seller: "${account}" status: 1} first:1000 orderBy: insertDate, orderDirection: desc) {
    id
    tokenContract
    tokenId
    price
    seller
    category
    status
    buyer
    saleToken
    saleIndex
    marketContract
  }`

  const result = await BaseGraphql(nftListQuery)
  return result.data.saleNFTs
}

export async function getAccountCollectionNFTItems(account: string, contracts: string[]) {
  const nftListQuery = `  tokens(where: {owner: "${account.toLowerCase()}", contract_in: [${contracts.join(
    ','
  )}]} first: 500) {
    id
    contract {
      id
    }
    tokenID
    tokenURI
    owner {
      id
    }
  }`

  const result = await BaseGraphql(nftListQuery, NFT_ERC721_GRAPH_URL)
  return result.data.tokens
}

export async function getAccountCollectionItems(account: string | null | undefined, contract: string | undefined) {
  let result = []
  if (account && contract) {
    const nftListQuery = `  tokens(where: {owner: "${account.toLowerCase()}", contract: "${contract.toLowerCase()}"} first: 500) {
    id
    contract {
      id
    }
    tokenID
    tokenURI
    owner {
      id
    }
  }`

    const graphResult = await BaseGraphql(nftListQuery, NFT_ERC721_GRAPH_URL)
    result = graphResult.data.tokens
  }
  return result
}

export async function getAccountCollectionNFTItemsWithPagination(
  account: string,
  contracts: string[],
  skip: number,
  perPage: number
) {
  const nftListQuery = `  tokens(
              where: {owner: "${account.toLowerCase()}", contract_in: [${contracts.join(',')}]} 
              first: ${perPage}, skip: ${skip}
              ) {
    id
    contract {
      id
    }
    tokenID
    tokenURI
    owner {
      id
    }
  }`

  const result = await BaseGraphql(nftListQuery, NFT_ERC721_GRAPH_URL)
  return result.data.tokens
}

function getAllActivitiesSortQuery(sortItems?: string) {
  let result = 'orderBy: timestamp, orderDirection: desc'

  if (sortItems) {
    if (sortItems === 'NewestItems') {
      result = 'orderBy: timestamp, orderDirection: desc'
    } else if (sortItems === 'OldestItems') {
      result = 'orderBy: timestamp, orderDirection: asc'
    } else if (sortItems === 'PriceLowToHigh') {
      result = 'orderBy: price, orderDirection: asc'
    } else if (sortItems === 'PriceHighToLow') {
      result = 'orderBy: price, orderDirection: desc'
    }
  }

  return result
}

export async function getAccountHistory(
  account: string,
  type: string,
  skip: number,
  sortItems?: string,
  currency?: string,
  nftCollection?: string
) {
  const actionFilter: string = type !== 'All' ? `action: "${type}",` : ''
  const userAccount = account.toLowerCase()
  let tokenFilter = ''
  let collectionFilter = ''
  if (nftCollection && nftCollection !== 'ALL') {
    collectionFilter = `tokenContract: "${nftCollection.toLowerCase()}",`
  }

  if (currency && currency !== 'ALL') {
    tokenFilter = `saleToken: "${currency}",`
  }
  const nftListQuery = ` histories(
  where: {${actionFilter} ${collectionFilter} ${tokenFilter} 
  account: "${userAccount}"} 
  ${getAllActivitiesSortQuery(sortItems)} 
  first: ${USER_PROFILE.ACTIVITY}, skip: ${skip}) {
    id
    account
    action
    tokenContract
    tokenId
    opponent
    price
    saleToken
    timestamp
    transactionHash
  }
`

  const result = await BaseGraphql(nftListQuery)
  return result.data.histories
}

export async function getTokenUri(address: string, tokenId: number) {
  const tokenUriQuery = `  tokens(where: {contract: "${address.toLowerCase()}", tokenID: ${tokenId}}) {tokenURI}`

  const result = await BaseGraphql(tokenUriQuery, NFT_ERC721_GRAPH_URL)
  return result.data.tokens[0]?.tokenURI
}

export async function getCollection(contract: string, skip: number) {
  const nftListQuery = `  tokens(where: {contract: "${contract.toLowerCase()}"} first: ${COLLECTION.LIST
    }, skip: ${skip}, orderBy: tokenID, orderDirection: asc) {
    id
    contract {
      id
    }
    tokenID
    tokenURI
    owner {
      id
    }
  }`

  const result = await BaseGraphql(nftListQuery, NFT_ERC721_GRAPH_URL)
  return result.data.tokens
}

export async function getCollectionHolders(contract: string) {
  const nftListQuery = `  tokenContracts(where: {id: "${contract.toLowerCase()}"}) {
    numTokens
    numOwners
  }`

  const result = await BaseGraphql(nftListQuery, NFT_ERC721_GRAPH_URL)
  return result.data.tokenContracts?.[0];
}


export async function getCollectionWithOnSale(contract: string, skip: number, onSale = false) {
  try {
    if (!onSale) {
      return await getCollection(contract, skip);
    } else {
      const tokenIdQuery = `  saleNFTs(where: {tokenContract: "${contract.toLowerCase()}", status: 1} first: ${COLLECTION.LIST
        }, skip: ${skip}, orderBy: tokenId, orderDirection: asc) {
        tokenId
      }`
      const tokenIdQueryResult = await BaseGraphql(tokenIdQuery)
      const tokenIds = tokenIdQueryResult.data.saleNFTs.map((item: any) => item.tokenId)
      const nftListQuery = `  tokens(where: {contract: "${contract.toLowerCase()}", tokenID_in: [${tokenIds.join(',')}]}, orderBy: tokenID, orderDirection: asc) {
          id
          contract {
            id
          }
          tokenID
          tokenURI
          owner {
            id
          }
        }`
      const result = await BaseGraphql(nftListQuery, NFT_ERC721_GRAPH_URL)
      return result.data.tokens
    }
  } catch (error) {
    return [];
  }
}

export async function getCollectionWithCount(contract: string, skip: number, take: number = 10) {
  const nftListQuery = `  tokens(where: {contract: "${contract.toLowerCase()}"} first: ${take
    }, skip: ${skip}, orderBy: tokenID, orderDirection: asc) {
    id
    contract {
      id
    }
    tokenID
    tokenURI
    owner {
      id
    }
  }`

  const result = await BaseGraphql(nftListQuery, NFT_ERC721_GRAPH_URL)
  return result.data.tokens
}

export async function getLastSoldNFTList(take: number) {
  const nftListQuery = ` saleNFTs(where: { status: 2 }, first: ${take}, orderBy: updateDate, orderDirection: desc) {
    id
    tokenContract
    tokenId
    price
    category
    seller
    saleIndex
    marketContract
    saleToken
  }`

  const result = await BaseGraphql(nftListQuery)
  const tokens = result.data.saleNFTs
  return tokens || []
}

export async function getTokenSale(contract: string, tokenId: number) {
  const nftListQuery = ` saleNFTs(where: {tokenContract: "${contract.toLowerCase()}" tokenId: ${tokenId} status: 1} orderBy: insertDate, orderDirection: desc) {
    id
    tokenContract
    tokenId
    price
    seller
    category
    status
    buyer
    saleToken
    saleIndex
    marketContract
  }`

  const result = await BaseGraphql(nftListQuery)
  const salesNFT = result.data.saleNFTs
  return salesNFT && salesNFT.length > 0 ? salesNFT[0] : null
}

export async function getCollectionPrice(contract: string, currency: string, type: string, status: number) {
  const nftListQuery = ` saleNFTs(where: {saleToken: "${currency.toLowerCase()}", tokenContract: "${contract.toLowerCase()}", status: ${status}}, orderBy: price, orderDirection: ${type}, first: 1) {
    price
  }`

  const result = await BaseGraphql(nftListQuery)
  const salesNFT = result.data.saleNFTs
  return salesNFT && salesNFT.length > 0 ? salesNFT[0].price : 0
}

export async function getCollectionTotalVolume(contract: string, currency: string) {
  const nftListQuery = ` saleNFTs(where: {saleToken: "${currency.toLowerCase()}", tokenContract: "${contract.toLowerCase()}", status: 2}, orderBy: price, first: 1000) {
    price
  }`

  const result = await BaseGraphql(nftListQuery)
  const salesNFT = result.data.saleNFTs
  return salesNFT && salesNFT.length > 0 ? salesNFT : []
}

export async function getAllActivities(
  type: string,
  skip: number,
  sortItems?: string,
  currency?: string,
  nftCollection?: string
) {
  const actionFilter: string = type !== 'All' ? `action: "${type}",` : ''
  let tokenFilter = ''
  let collectionFilter = ''

  if (nftCollection && nftCollection !== 'ALL') {
    collectionFilter = `tokenContract: "${nftCollection.toLowerCase()}",`
  }

  if (currency && currency !== 'ALL') {
    tokenFilter = `saleToken: "${currency}",`
  }

  const nftListQuery = ` histories(where: {${actionFilter} ${collectionFilter} ${tokenFilter} action_not: "Sold"}
  ${getAllActivitiesSortQuery(sortItems)} 
  , first: ${MARKET_PLACE.ACTIVITY}, skip: ${skip}) {
    id
    account
    action
    tokenContract
    tokenId
    opponent
    price
    saleToken
    timestamp
    transactionHash
  }
`

  const result = await BaseGraphql(nftListQuery)
  return result.data.histories
}

export async function getStatistics() {
  const statisticQuery = `statistics {
    id
    saleCountWithYTS
    saleCountWithAVAX
    totalSaleAmountWithYTS
    totalSaleAmountWithAVAX
  }`

  const result = await BaseGraphql(statisticQuery)
  const statistic = result.data.statistics
  return statistic && statistic.length > 0 ? statistic[0] : null
}
